import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Globe, Truck, Package, Shield, Clock, MapPin, ArrowRight, CheckCircle2 } from 'lucide-react';
import Reveal from '../components/Reveal';

const Services = () => {
  const mainServices = [
    {
      icon: Globe,
      title: 'International Shipping',
      description: 'Comprehensive global freight solutions connecting your business to markets worldwide.',
      features: [
        'Air freight services',
        'Ocean freight (FCL & LCL)',
        'Door-to-door delivery',
        'Customs clearance',
        'Documentation support',
      ],
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80',
    },
    {
      icon: Truck,
      title: 'Last-Mile Delivery',
      description: 'Fast, reliable final-leg delivery ensuring your packages reach their destination.',
      features: [
        'Same-day delivery options',
        'Scheduled deliveries',
        'Proof of delivery',
        'Real-time tracking',
        'Flexible delivery windows',
      ],
      image: 'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=800&q=80',
    },
  ];

  const additionalServices = [
    {
      icon: Package,
      title: 'Freight Forwarding',
      description: 'Expert coordination of shipments across multiple carriers and borders.',
    },
    {
      icon: Shield,
      title: 'Cargo Insurance',
      description: 'Comprehensive coverage to protect your valuable shipments.',
    },
    {
      icon: Clock,
      title: 'Express Services',
      description: 'Priority handling for time-critical shipments.',
    },
    {
      icon: MapPin,
      title: 'Warehousing',
      description: 'Secure storage solutions with inventory management.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-gray-50 py-20">
        <div className="pointer-events-none absolute -top-24 -right-16 w-96 h-96 bg-emerald-200/40 rounded-full blur-3xl animate-blob" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 w-80 h-80 bg-red-200/25 rounded-full blur-3xl animate-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Our <span className="gradient-text">Services</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Comprehensive logistics solutions tailored to your business needs. From international freight to your customer's doorstep, we handle it all.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {mainServices.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              return (
                <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  <Reveal className={isEven ? '' : 'lg:order-2'}>
                    <div className="inline-flex items-center space-x-3 mb-6">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <Icon className="text-emerald-600" size={24} />
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                    </div>
                    <p className="text-lg text-gray-600 mb-8">{service.description}</p>
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="text-emerald-600 mt-1 flex-shrink-0" size={20} />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/contact">
                      <Button className="group bg-red-600 hover:bg-red-700 text-white shadow-sm hover:shadow-md transition-all active:scale-95">
                        Learn More
                        <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={18} />
                      </Button>
                    </Link>
                  </Reveal>
                  <Reveal delay={120} className={`overflow-hidden rounded-2xl shadow-2xl ${isEven ? '' : 'lg:order-1'}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full transition-transform duration-500 hover:scale-105"
                    />
                  </Reveal>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Additional Services
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive solutions to support your logistics needs
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Reveal key={index} delay={index * 100}>
                  <Card className="h-full border-2 hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 group">
                    <CardContent className="p-6 space-y-4">
                      <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center transition-colors group-hover:bg-emerald-500">
                        <Icon className="text-emerald-600 transition-colors group-hover:text-white" size={28} />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20 bg-emerald-600">
        <div className="pointer-events-none absolute -top-20 -right-10 w-80 h-80 bg-emerald-400/40 rounded-full blur-3xl animate-blob" aria-hidden="true" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Contact us today to discuss your logistics needs and receive a personalized quote.
          </p>
          <Link to="/contact">
            <Button className="group bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all active:scale-95">
              Request a Quote
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={20} />
            </Button>
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Services;